import React, { useEffect, useState } from 'react';

import './timer.css';

type TimerProps = {
  initialSeconds: number;
  pausable?: boolean;
  hide?: boolean;
  onComplete: () => void;
};

// Geometry of the progress ring (viewBox is 100x100).
const RING_RADIUS = 44;
const RING_LENGTH = 2 * Math.PI * RING_RADIUS;

export const Timer = ({ initialSeconds, onComplete, pausable, hide }: TimerProps) => {
  const [lastUpdate, setLastUpdate] = useState<number>(Date.now());
  const [timeLeft, setTimeLeft] = useState<number>(initialSeconds * 1000);
  const [isPaused, setIsPaused] = useState<boolean>(false);
  const [isActive, setIsActive] = useState<boolean>(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (isPaused) {
        setLastUpdate(Date.now());
        return;
      }
      const now = Date.now();
      const newTimeLeft = timeLeft - (now - lastUpdate);
      if (newTimeLeft > 0) {
        setTimeLeft(newTimeLeft);
        setLastUpdate(now);
      } else {
        setTimeLeft(0);
        clearInterval(interval);
        onComplete();
      }
    }, 20);

    return () => {
      clearInterval(interval);
    };
  }, [isPaused]);

  useEffect(() => {
    const initTimeout = setTimeout(() => {
      setIsActive(true);
    }, 100);

    return () => {
      clearTimeout(initTimeout);
    };
  }, []);

  const togglePause = () => {
    setIsPaused(!isPaused);
  };

  const totalSeconds = Math.max(0, Math.floor(timeLeft / 1000));
  const min = Math.floor(totalSeconds / 60);
  let sec: number | string = totalSeconds % 60;
  if (sec < 10) {
    sec = '0' + sec;
  }

  const totalMs = Math.max(1, initialSeconds * 1000);
  const fraction = Math.min(1, Math.max(0, timeLeft / totalMs));
  const urgent = totalSeconds <= 10;

  return (
    <div className={'timer' + (isActive ? ' go' : '') + (hide ? ' hide' : '') + (urgent ? ' urgent' : '')}>
      <svg className='timerRing' viewBox='0 0 100 100' aria-hidden='true'>
        <circle className='timerTrack' cx='50' cy='50' r={RING_RADIUS} />
        <circle
          className='timerProgress'
          cx='50'
          cy='50'
          r={RING_RADIUS}
          strokeDasharray={RING_LENGTH}
          strokeDashoffset={RING_LENGTH * (1 - fraction)}
        />
      </svg>
      <span className='timerText'>{`${min}:${sec}`}</span>
      {pausable && (
        <span className='pause' onClick={togglePause}>
          {isPaused ? '\u25B6' : '\u2016'}
        </span>
      )}
    </div>
  );
};
